import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { TextareaField } from '@/components/form/fields/textarea-field';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';
import { CreateTaskComment } from '@/task-manager/api/mutations/create-task-comment';
import type { TaskDetails } from '@/task-manager/entities/task-details';
import type { TaskPreview } from '@/task-manager/entities/task-preview';
import { TaskStatus } from '@/task-manager/entities/task-status';
import { canSubmitResetRequest } from '@/task-manager/utils/safety-score';

export type AlertFeedbackDialogProps = {
  newStatus: TaskStatus;
  task: TaskPreview | TaskDetails;
} & Pick<DialogProps, 'open' | 'onClose'>;

export function AlertFeedbackDialog({
  newStatus,
  task,
  open,
  onClose,
}: AlertFeedbackDialogProps) {
  const form = useForm<FormInputs>({
    resolver: zodResolver(formSchema),
  });
  const createComment = CreateTaskComment.useMutation();
  const { isValid } = form.formState;
  async function handleSubmit(data: FormInputs) {
    await toast.promise(
      createComment.mutateAsync({
        taskId: task.id,
        ...data,
      }),
      {
        pending: 'Posting comment...',
        error: 'An unexpected error happened. Please try again.',
        success: {
          render: () => 'Comment saved successfully.',
          hideProgressBar: true,
          autoClose: 3000,
        },
      },
    );
    onClose(false);
  }

  if (!canSubmitResetRequest(task, newStatus)) {
    return null;
  }

  return (
    <Dialog
      className="mt-24"
      open={open}
      onClose={onClose}
      data-testid="task-alert-feedback-dialog"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogTitle>Provide feedback on the alert</DialogTitle>
          <DialogDescription className="flex flex-col gap-3">
            <p>
              To improve our services we would like your feedback on the alert:
              <ul className="mt-1 list-disc pl-6">
                <li>Did the alert correspond to a real issue in the device?</li>
                <li>Was the suggested action helpful?</li>
                <li>Which steps were taken to address the issue?</li>
              </ul>
            </p>
            <p>Your feedback will be stored as a comment.</p>
          </DialogDescription>
          <DialogBody className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comment</FormLabel>
                  <FormControl>
                    <TextareaField {...field} data-testid="feedback-textarea" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>
          <DialogActions>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onClose(false)}
            >
              Skip
            </Button>
            <Button
              variant="primaryOutline"
              type="submit"
              disabled={!isValid || createComment.isLoading}
              data-testid="feedback-submit"
            >
              Add comment
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}

const formSchema = z.object({
  content: z.string().min(1),
});

type FormInputs = z.infer<typeof formSchema>;
